// Copyright (c) Magnopus LLC. All Rights Reserved.

import type { ReactNode } from 'react';
import { Suspense, useRef, useState } from 'react';
import * as ReactDOM from 'react-dom/client';

import { all } from '@awesome.me/kit-1122aa6ba3/icons';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { library } from '@fortawesome/fontawesome-svg-core';
import { lazyRetry } from '@magnopus/shared-utils';
import { initNodeyAccessTokenSync } from '@magnopus/space-state';
import { NewVersionAvailableContext, olympusService } from '@magnopus/utils';

import { register } from './serviceWorker';
import { initSentry } from './utils/initSentry';

library.add(...(all as IconDefinition[]));

initNodeyAccessTokenSync(olympusService.subscribeToAccessToken, olympusService.getAccessToken);

const App = lazyRetry(() => import('./app/app'), 'App');
const el = document.getElementById('root');
if (el?.style) {
  el.style.background = '#000';
}

const sentryRootOptions = initSentry();

const root = ReactDOM.createRoot(el as HTMLElement, sentryRootOptions);

function NewVersionTracker({ children }: { children: ReactNode }) {
  const [newVersionInstalling, setNewVersionInstalling] = useState<boolean>(false);
  const [newVersionAvailable, setNewVersionAvailable] = useState<boolean>(false);
  const [highlightingNewVersion, setHighlightingNewVersion] = useState<boolean>(false);

  const newVersionAvailableCallback = (state: 'installing' | 'installed') => {
    if (state === 'installing') {
      setNewVersionInstalling(true);
    } else if (state === 'installed') {
      setNewVersionInstalling(false);
      setNewVersionAvailable(true);
    }
  };

  register(newVersionAvailableCallback);

  const timeout = useRef<NodeJS.Timeout | undefined>(undefined);
  const activateHighlight = (period?: number) => {
    setHighlightingNewVersion(true);
    if (timeout.current) {
      clearTimeout(timeout.current);
    }
    timeout.current = setTimeout(() => {
      timeout.current = undefined;
      setHighlightingNewVersion(false);
    }, (period ?? 1) * 1000);
  };

  return (
    <NewVersionAvailableContext.Provider
      value={{
        newVersionInstalling,
        newVersionAvailable,
        highlightingNewVersion,
        activateHighlight,
        triggerNewVersion: () => newVersionAvailableCallback('installed')
      }}
    >
      {children}
    </NewVersionAvailableContext.Provider>
  );
}

root.render(
  <NewVersionTracker>
    <Suspense
      fallback={
        <div className="loading-screen">
          <progress className="loading-progress-circular" />
        </div>
      }
    >
      <App />
    </Suspense>
  </NewVersionTracker>
);
